import { ReactNode, useRef, useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTimeTrackingContext } from '@/hooks/useTimeTrackingContext'
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext'
import UserInitials from './UserInitials'
import Logo from './Logo'
import GlobalScrollEffects from './GlobalScrollEffects'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const { runningEntry } = useTimeTrackingContext()
  const { displaySettings } = useUserSettingsContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleLogout = async () => {
    await logout()
  }

  // Close menu on click outside or Escape
  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [menuOpen])

  // Handle scroll detection for sticky header with smooth effect
  useEffect(() => {
    function handleScroll() {
      const scrollTop = window.scrollY
      // More gradual transition - start effect earlier and make it smoother
      setIsScrolled(scrollTop > 5)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
    }, [])

  const isActive = (path: string) => {
    return location.pathname === path
  }





  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ease-out will-change-transform ${
        isScrolled 
          ? 'backdrop-blur-md shadow-xl border-b border-gray-200/50 transform translate-y-0' 
          : 'backdrop-blur-sm shadow-sm border-b border-gray-200/30 transform translate-y-0'
      } ${runningEntry && displaySettings.showActiveEntryAnimations ? 'animate-header-pulse-optimized' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <Logo />
              
              {user && (
                <nav className="flex space-x-6">
                  <Link
                    to="/"
                    className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                      isActive('/') 
                        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/entries"
                    className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                      isActive('/entries') 
                        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                    }`}
                  >
                    Entries
                  </Link>
                  <Link
                    to="/projects"
                    className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                      isActive('/projects') 
                        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                    }`}
                  >
                    Projects
                  </Link>
                  <Link
                    to="/calendar"
                    className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                      isActive('/calendar') 
                        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                    }`}
                  >
                    Calendar
                  </Link>
                  <Link
                    to="/reporting"
                    className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                      isActive('/reporting') 
                        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                    }`}
                  >
                    Reporting
                  </Link>
                </nav>
              )}
            </div>
            
            {user && (
              <div className="flex items-center space-x-4">
                {/* Information Icon */}
                <Link
                  to="/information"
                  className={`text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105 ${
                    isActive('/information') 
                      ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
                  }`}
                  title="Information & Help"
                  aria-label="Information and help"
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </Link>
                <div
                  className="relative"
                  onClick={(e) => {
                    // Only toggle if clicking on the UserInitials itself, not the menu
                    if (e.target === e.currentTarget || e.target === e.currentTarget.firstElementChild) {
                      setMenuOpen((open) => !open);
                    }
                  }}
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={menuOpen}
                  ref={menuRef}
                >
                  <UserInitials 
                    name={user.name} 
                    size="md"
                    className="bg-transparent text-gray-400 border border-gray-300 cursor-pointer hover:bg-gray-100 hover:scale-110 hover:shadow-md transition-all duration-300 ease-out transform"
                    color="#9ca3af"
                  />
                  <div className={`absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md border border-gray-200/50 rounded-lg shadow-xl z-50 py-2 transition-all duration-300 ease-out ${
                    menuOpen 
                      ? 'opacity-100 transform translate-y-0 scale-100' 
                      : 'opacity-0 transform -translate-y-2 scale-95 pointer-events-none'
                  }`}>
                    <div className="px-4 py-2 text-gray-900 text-sm font-medium border-b border-gray-100">{user.name}</div>
                    {user.role === 'admin' && (
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/80 hover:backdrop-blur-sm transition-all duration-200 ease-out transform hover:translate-x-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          navigate('/role-management');
                        }}
                      >
                        Role Management
                      </button>
                    )}
                    {(user.role === 'admin' || user.role === 'reader') && (
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/80 hover:backdrop-blur-sm transition-all duration-200 ease-out transform hover:translate-x-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          navigate('/entry-management');
                        }}
                      >
                        Entry Management
                      </button>
                    )}
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/80 hover:backdrop-blur-sm transition-all duration-200 ease-out transform hover:translate-x-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        navigate('/settings');
                      }}
                    >
                      Settings
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/80 hover:backdrop-blur-sm transition-all duration-200 ease-out transform hover:translate-x-1"
                      type="button"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content with enhanced scrolling effects */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pt-24">
        <GlobalScrollEffects>
          {children}
        </GlobalScrollEffects>
      </main>
    </div>
  )
} 